<template>
    <!-- No top padding: every other module opens its header flush with the top
         of the container (see .atlhr-dashboard-title pages), so `p-3` here left
         the CTC title sitting 16px lower than the rest of the app. -->
    <div class="px-3 pb-3">
        <!-- Breadcrumb and Page Header -->
        <div class="ctc-header-section mb-4">
            <h1 class="ctc-page-title">Cost to Company</h1>
            <div class="ctc-breadcrumb">
                <span class="breadcrumb-item-link">Dashboard</span> /
                <span class="breadcrumb-item-active">Cost to Company</span>
                <template v-if="!isDashboardTab"> / <span class="breadcrumb-item-active">{{ currentTabLabel }}</span></template>
            </div>
        </div>

        <!-- The loading overlay covers the tab content only. Wrapping the whole
             page greyed out the title, breadcrumb and tab strip as well. -->
        <b-tabs :class="['ctc-tabs', { 'first-tab-active': activeTabIndex === 0, 'last-tab-active': isLastTabActive }]" v-model="activeTabIndex">
            <b-tab key="dashboard" v-if="(!ctcSettings || ctcSettings.mode != 2) && $acl && $acl['cost-to-company'] >= 1" title="Dashboard" lazy>
                <b-overlay :show="showOverlay" rounded="sm"><ctc-dashboard></ctc-dashboard></b-overlay>
            </b-tab>
            <b-tab key="employees" v-if="(!ctcSettings || ctcSettings.mode != 2) && $acl && $acl['cost-to-company'] >= 1" title="Employees" lazy>
                <b-overlay :show="showOverlay" rounded="sm"><ctc-ctc-employee></ctc-ctc-employee></b-overlay>
            </b-tab>
            <b-tab key="reports" v-if="$acl && $acl['cost-to-company'] >= 1" title="Annual Reports" lazy>
                <b-overlay :show="showOverlay" rounded="sm"><ctc-annual-report :ctc-settings="ctcSettings"></ctc-annual-report></b-overlay>
            </b-tab>
            <b-tab key="heads" v-if="$acl && $acl['cost-to-company'] >= 1" title="CTC Heads" lazy>
                <b-overlay :show="showOverlay" rounded="sm"><ctc-configuration :ctc-settings="ctcSettings"></ctc-configuration></b-overlay>
            </b-tab>
            <b-tab key="settings" v-if="$acl && $acl['cost-to-company'] >= 1" title="CTC Settings" lazy>
                <b-overlay :show="showOverlay" rounded="sm"><ctc-settings :ctc-settings="ctcSettings" @settings-updated="loadSettings"></ctc-settings></b-overlay>
            </b-tab>
            <b-tab key="remuneration" v-if="$acl && $acl['cost-to-company'] >= 1" title="Remuneration Settings" lazy>
                <b-overlay :show="showOverlay" rounded="sm"><ctc-ctc-settings></ctc-ctc-settings></b-overlay>
            </b-tab>
            <b-tab key="audit" v-if="(!ctcSettings || ctcSettings.mode != 2) && $acl && $acl['cost-to-company'] >= 1" title="Audit Logs" lazy>
                <b-overlay :show="showOverlay" rounded="sm"><ctc-audit-report></ctc-audit-report></b-overlay>
            </b-tab>
        </b-tabs>
    </div>
</template>

<script>

    export default {
        data() {
            return {
                // uids of the components currently asking for the overlay. A plain
                // boolean here let whichever component finished first clear an
                // overlay another one still needed.
                overlayHolders: [],
                ctcSettings: null,
                activeTabIndex: 0,
                activeTabId: sessionStorage.getItem('ctc_active_tab') || ''
            };
        },
        watch: {
            activeTabIndex(newVal) {
                const ids = this.getVisibleTabIds();
                if (ids[newVal]) {
                    this.activeTabId = ids[newVal];
                    sessionStorage.setItem('ctc_active_tab', this.activeTabId);
                }
                // The tabs are `lazy`, so leaving one destroys its contents. Any
                // hold those components still had is released by their own
                // beforeDestroy, but drop the whole set as well so a stale hold
                // from a component that was already gone cannot outlive the tab.
                this.overlayHolders = [];
            }
        },
        computed: {
            showOverlay() {
                return this.overlayHolders.length > 0;
            },
            // On the Dashboard tab the trailing crumb repeats the leading one
            // ("Dashboard / Cost to Company / Dashboard"), so it is dropped.
            isDashboardTab() {
                return this.currentTabLabel === 'Dashboard';
            },
            // The active tab now sits flush with the card edges, so the first and
            // last tabs have to square off the corner they cover.
            isLastTabActive() {
                return this.activeTabIndex === this.getVisibleTabIds().length - 1;
            },
            currentTabLabel() {
                const ids = this.getVisibleTabIds();
                const activeId = ids[this.activeTabIndex];
                switch (activeId) {
                    case 'dashboard':
                        return 'Dashboard';
                    case 'employees':
                        return 'Employee CTC Management';
                    case 'reports':
                        return 'Annual Reports';
                    case 'heads':
                        return 'CTC Heads';
                    case 'settings':
                        return 'CTC Settings';
                    case 'remuneration':
                        return 'Remuneration Settings';
                    case 'audit':
                        return 'Audit Logs';
                    default:
                        return 'Dashboard';
                }
            }
        },
        created() {
            this.$root.$on('ctc-show-overlay', this.onOverlayRequest);

            const ids = this.getVisibleTabIds();
            let index = ids.indexOf(this.activeTabId);
            if (index === -1 && ids.length > 0) {
                this.activeTabId = ids[0];
                index = 0;
            }
            if (index !== -1) {
                this.activeTabIndex = index;
            }
        },
        beforeDestroy() {
            // Only this instance's handler: the bare $off() dropped every listener
            // registered on $root for the event, including a newer Index's.
            this.$root.$off('ctc-show-overlay', this.onOverlayRequest);
        },
        mounted() {
            this.loadSettings();
        },
        methods: {
            // `source` is the emitting component's uid, sent by the ctcOverlay()
            // helper in ../js/ctcOverlay.js. Emits that predate it share one
            // bucket, which is no worse than the boolean they used to set.
            onOverlayRequest(val, source) {
                const key = source == null ? 'anonymous' : String(source);
                const index = this.overlayHolders.indexOf(key);
                if (val) {
                    if (index === -1) {
                        this.overlayHolders.push(key);
                    }
                } else if (index !== -1) {
                    this.overlayHolders.splice(index, 1);
                }
            },
            getVisibleTabIds() {
                const ids = [];
                if (!this.ctcSettings || this.ctcSettings.mode != 2) {
                    ids.push('dashboard');
                }
                if (!this.ctcSettings || this.ctcSettings.mode != 2) {
                    ids.push('employees');
                }
                ids.push('reports', 'heads', 'settings', 'remuneration');
                if (!this.ctcSettings || this.ctcSettings.mode != 2) {
                    ids.push('audit');
                }
                return ids;
            },
            loadSettings() {
                axios.get('/cost-to-company/get-settings')
                    .then(response => {
                        if (response.data && response.data.ctcSettings) {
                            const prevTabId = this.activeTabId;
                            this.ctcSettings = response.data.ctcSettings;
                            this.$nextTick(() => {
                                const ids = this.getVisibleTabIds();
                                let newIndex = ids.indexOf(prevTabId);
                                if (newIndex === -1 && ids.length > 0) {
                                    newIndex = 0;
                                }
                                if (newIndex !== -1) {
                                    this.activeTabIndex = newIndex;
                                }
                            });
                        }
                    })
                    .catch(error => {
                        console.error('Error loading settings in Index:', error);
                    });
            }
        }
    }
</script>

<style scoped>
.ctc-header-section {
    padding-left: 4px;
    padding-top: 50px;
}
/* .ctc-page-title / .ctc-breadcrumb are global (resources/sass/_ctc.scss). */

/* The strip spans the full width of the card: every tab is an equal-width
   flex track, so the last tab ends flush with the card's right edge. */
.ctc-tabs ::v-deep .nav-tabs {
    border-bottom: none;
    margin-bottom: 12px;
    padding-left: 0;
    display: flex;
    flex-wrap: nowrap;
    width: 100%;
}
.ctc-tabs ::v-deep .nav-item:first-child .nav-link.active::before {
    display: none !important;
}
/* Mirror image of the first-tab rule: at full width the last tab touches the
   card's right edge, so its outward notch would hang off the card. */
.ctc-tabs ::v-deep .nav-item:last-child .nav-link.active::after {
    display: none !important;
}
.ctc-tabs ::v-deep .nav-item {
    margin-bottom: 0;
    /* `1 1 0` (not `1 1 auto`) so long titles like "Remuneration Settings"
       don't claim a wider track than the short ones. */
    flex: 1 1 0;
    min-width: 0;
    display: flex;
}
.ctc-tabs ::v-deep .nav-item:last-child .nav-link {
    margin-right: 0 !important;
}
.ctc-tabs ::v-deep .nav-link {
    border: 1px solid #e2e8f0 !important;
    background-color: #ffffff !important;
    color: #8F8F8F !important;
    font-weight: 500 !important;
    padding: 10px 12px !important;
    border-radius: 12px !important;
    margin-right: 8px !important;
    transition: all 0.15s ease-in-out !important;
    /* Same elevation the Leave & Attendance tabs use (atlhr.scss), so the CTC
       strip reads as part of the same system. */
    box-shadow: 0px 0px 25px 0px rgba(1, 82, 75, 0.15) !important;
    /* Stretched to the row height with the label pinned to the top, so a tab
       that wraps to two lines keeps every other tab's baseline aligned. */
    width: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    text-align: center;
    white-space: normal;
    font-size: 14px !important;
    line-height: 1.3 !important;
}
.ctc-tabs ::v-deep .nav-link:hover {
    border-color: #cbd5e1 !important;
    color: #34444C !important;
    text-decoration: none !important;
}
.ctc-tabs ::v-deep .nav-link.active {
    background-color: #ffffff !important;
    color: #0892A4 !important;
    border-color: #E8E8E8 !important;
    border-bottom: 1px solid #ffffff !important;
    border-radius: 12px 12px 0 0 !important;
    margin-bottom: -13px !important;
    padding-bottom: 22px !important;
    position: relative !important;
    z-index: 2 !important;
    /* The active tab is fused into the card below it; a glow here would paint a
       smudge across that join (it sits above .tab-content in the stack). The
       card's own shadow carries the elevation for it. */
    box-shadow: none !important;
}
.ctc-tabs ::v-deep .nav-link.active::before {
    content: "";
    position: absolute;
    bottom: 0;
    left: -12px;
    width: 12px;
    height: 12px;
    border-bottom-right-radius: 12px;
    box-shadow: 5px 5px 0 0 #ffffff;
    border-right: 1px solid #E8E8E8;
    border-bottom: 1px solid #E8E8E8;
    pointer-events: none;
}
.ctc-tabs ::v-deep .nav-link.active::after {
    content: "";
    position: absolute;
    bottom: 0;
    right: -12px;
    width: 12px;
    height: 12px;
    border-bottom-left-radius: 12px;
    box-shadow: -5px 5px 0 0 #ffffff;
    border-left: 1px solid #E8E8E8;
    border-bottom: 1px solid #E8E8E8;
    pointer-events: none;
}
.ctc-tabs ::v-deep .tab-content {
    background-color: #ffffff;
    border: 1px solid #E8E8E8;
    border-radius: 16px;
    box-shadow: 0px 0px 25px 0px rgba(1, 82, 75, 0.15);
    padding: 24px;
    position: relative;
    z-index: 1;
}
.ctc-tabs.first-tab-active ::v-deep .tab-content {
    border-top-left-radius: 0 !important;
}
.ctc-tabs.last-tab-active ::v-deep .tab-content {
    border-top-right-radius: 0 !important;
}

/* Seven equal tracks stop being readable well before the phone breakpoint,
   so tighten the labels first, then fall back to a wrapped pill grid. */
@media (max-width: 1400px) {
    .ctc-tabs ::v-deep .nav-link {
        padding-left: 8px !important;
        padding-right: 8px !important;
        font-size: 13px !important;
    }
}
@media (max-width: 991.98px) {
    .ctc-tabs ::v-deep .nav-tabs {
        flex-wrap: wrap;
    }
    .ctc-tabs ::v-deep .nav-item {
        flex: 1 1 33.333%;
        margin-bottom: 8px;
    }
    /* Once the strip wraps, only the tabs on the last row could connect to the
       card, so drop the notch-and-overlap treatment and use plain pills. */
    .ctc-tabs ::v-deep .nav-item:last-child .nav-link {
        margin-right: 8px !important;
    }
    .ctc-tabs ::v-deep .nav-link {
        align-items: center;
    }
    .ctc-tabs ::v-deep .nav-link.active {
        border-color: #E8E8E8 !important;
        border-bottom: 1px solid #E8E8E8 !important;
        border-radius: 12px !important;
        margin-bottom: 0 !important;
        padding-bottom: 10px !important;
        align-items: center;
        /* Detached from the card at this width, so it takes the glow back. */
        box-shadow: 0px 0px 25px 0px rgba(1, 82, 75, 0.15) !important;
    }
    .ctc-tabs ::v-deep .nav-link.active::before,
    .ctc-tabs ::v-deep .nav-link.active::after {
        display: none !important;
    }
    .ctc-tabs.first-tab-active ::v-deep .tab-content,
    .ctc-tabs.last-tab-active ::v-deep .tab-content {
        border-top-left-radius: 16px !important;
        border-top-right-radius: 16px !important;
    }
}
@media (max-width: 575.98px) {
    .ctc-tabs ::v-deep .nav-item {
        flex: 1 1 50%;
    }
    .ctc-tabs ::v-deep .tab-content {
        padding: 16px !important;
    }
}
</style>
